/**
 * Contract Edit Page
 * Thin wrapper that uses the ContractWizard component
 * Loads contract data and passes it to the wizard
 */

import React, { useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { ContractWizard } from '../components/contracts/wizard';
import { apiGet } from '../utils/apiClient';
import useAsyncData from '../hooks/useAsyncData';

function ContractEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial step from navigation state (e.g., after schedule generation)
  const initialStep = location.state?.initialStep;
  const scheduleJustGenerated = location.state?.scheduleJustGenerated || false;

  // Clear the one-shot navigation state so a refresh doesn't replay the toast
  useEffect(() => {
    if (location.state?.scheduleJustGenerated) {
      navigate(location.pathname, { replace: true, state: { initialStep } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: contract, loading, error } = useAsyncData(
    useCallback(() => apiGet(`/contracts/${id}`), [id]),
    { defaultValue: null, deps: [id] }
  );

  // Redirect non-editable contracts to detail view once loaded
  useEffect(() => {
    if (!loading && contract && contract.status && !['Draft', 'Pending Approval'].includes(contract.status)) {
      navigate(`/contracts/${id}`, { replace: true });
    }
  }, [loading, contract, id, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Contract not found or cannot be edited'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/contracts')}>
          Back to Contracts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ContractWizard
        mode="edit"
        contractId={id}
        initialData={contract}
        initialStep={initialStep}
        scheduleJustGenerated={scheduleJustGenerated}
      />
    </Box>
  );
}

export default ContractEdit;
